import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import opdlogo from "../../assests/images/pccarelogo.jpg";
import { getRequest } from "../../apis/ApiInstence";
import { shiprocketInvoice } from "../../apis/shiprocket";

const MyorderDetails = () => {
  const [data, setData] = useState([]);
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const params = useParams();
  const contentRef = useRef(null);
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const cleaned = String(value).replace(/[^\d.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  };

  const getItemQty = (item) => {
    const qty = toNumber(
      item?.quantity ??
        item?.qty ??
        item?.product_qty ??
        item?.productQty ??
        1,
    );
    return qty || 1;
  };

  const getItemPrice = (item) => {
    const direct = toNumber(
      item?.price ??
        item?.unit_price ??
        item?.product_price ??
        item?.product?.price ??
        item?.product?.selling_price ??
        item?.product?.offer_price ??
        item?.product?.discount_price ??
        0,
    );
    if (direct) return direct;
    const subtotal = toNumber(item?.subtotal ?? item?.total ?? item?.amount ?? 0);
    const qty = getItemQty(item);
    if (subtotal && qty) return subtotal / qty;
    return 0;
  };

  const formatCurrency = (value) => {
    const amount = toNumber(value);
    if (!Number.isFinite(amount)) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const calculateTotal = (items) =>
    items?.reduce((total, item) => total + getItemPrice(item) * getItemQty(item), 0) || 0;

  const looksLikeItem = (item) =>
    item &&
    (item?.product ||
      item?.product_id ||
      item?.productId ||
      item?.product_name ||
      item?.name) &&
    (item?.quantity ||
      item?.qty ||
      item?.price ||
      item?.unit_price ||
      item?.subtotal ||
      item?.amount ||
      item?.total);

  const tryParseJson = (value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return value;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  };

  const pickFirstItemsArray = (value) => {
    if (!value) return [];
    const parsed = tryParseJson(value);
    if (Array.isArray(parsed) && parsed.some(looksLikeItem)) return parsed;
    if (typeof parsed !== "object") return [];
    for (const entry of Object.values(parsed)) {
      const parsedEntry = tryParseJson(entry);
      if (Array.isArray(parsedEntry) && parsedEntry.some(looksLikeItem)) return parsedEntry;
    }
    return [];
  };

  const resolveInvoiceItems = (payload) => {
    if (!payload || typeof payload !== "object") return [];
    const candidates = [
      payload?.itemData1,
      payload?.order_items,
      payload?.orderDetails,
      payload?.orderDetails?.items,
      payload?.orderItems,
      payload?.order_item,
      payload?.items,
      payload?.order?.order_items,
      payload?.order?.orderDetails,
      payload?.order?.items,
      payload?.order?.products,
      payload?.order?.orderProducts,
      payload?.order_list_detail,
      payload?.order_list_details,
      payload?.orderListDetail,
      payload?.orderListDetails,
      payload?.data?.itemData1,
      payload?.data?.order_items,
      payload?.data?.orderDetails,
      payload?.data?.items,
      payload?.data?.order_list_detail,
      payload?.data?.order_list_details,
      payload?.data?.orderListDetail,
      payload?.data?.orderListDetails,
    ];
    for (const candidate of candidates) {
      const found = pickFirstItemsArray(candidate);
      if (found.length) return found;
    }
    const deepFound = pickFirstItemsArray(payload);
    if (deepFound.length) return deepFound;
    if (payload?.data) {
      const nestedFound = pickFirstItemsArray(payload.data);
      if (nestedFound.length) return nestedFound;
    }
    const deepSearch = (node, depth = 0, seen = new Set()) => {
      if (!node || typeof node !== "object" || depth > 6) return [];
      if (seen.has(node)) return [];
      seen.add(node);
      const parsedNode = tryParseJson(node);
      if (Array.isArray(parsedNode)) {
        if (parsedNode.some(looksLikeItem)) return parsedNode;
        for (const entry of parsedNode) {
          const found = deepSearch(entry, depth + 1, seen);
          if (found.length) return found;
        }
        return [];
      }
      if (typeof parsedNode !== "object") return [];
      if (Array.isArray(node)) {
        if (node.some(looksLikeItem)) return node;
        for (const entry of node) {
          const found = deepSearch(entry, depth + 1, seen);
          if (found.length) return found;
        }
        return [];
      }
      for (const value of Object.values(parsedNode)) {
        const found = deepSearch(value, depth + 1, seen);
        if (found.length) return found;
      }
      return [];
    };
    const deep = deepSearch(payload);
    if (deep.length) return deep;
    const findAnyArray = (node, depth = 0, seen = new Set()) => {
      if (!node || typeof node !== "object" || depth > 6) return [];
      if (seen.has(node)) return [];
      seen.add(node);
      const parsedNode = tryParseJson(node);
      if (Array.isArray(parsedNode)) {
        if (parsedNode.some((entry) => entry && typeof entry === "object")) return parsedNode;
        return [];
      }
      if (typeof parsedNode !== "object") return [];
      for (const value of Object.values(parsedNode)) {
        const found = findAnyArray(value, depth + 1, seen);
        if (found.length) return found;
      }
      return [];
    };
    const anyArray = findAnyArray(payload);
    if (anyArray.length) return anyArray;
    return [];
  };

  const invoiceItems = resolveInvoiceItems(data);
  const fallbackTotal = toNumber(
    data?.order?.total_amount ??
      data?.order?.totalAmount ??
      data?.total_amount ??
      data?.amount ??
      0,
  );
  const invoiceTotal = invoiceItems.length ? calculateTotal(invoiceItems) : fallbackTotal;

  const resolveShiprocketId = () => {
    const candidates = [
      data?.order?.shiprocket_order_id,
      data?.order?.shiprocketOrderId,
      data?.order?.shiprocket_id,
      data?.order?.sr_order_id,
      data?.order?.srOrderId,
      data?.order?.shiprocket_orderid,
      data?.order?.orderId,
      data?.order?.order_id,
      data?.order?.id,
      params?.id,
    ];
    return (
      candidates
        .map((value) => (value !== undefined && value !== null ? String(value).trim() : ""))
        .find(Boolean) || ""
    );
  };

  const buildInvoicePayload = () => {
    const shiprocketId = resolveShiprocketId();
    const orderId =
      (data?.order?.order_id || data?.order?.orderId || data?.order?.id || params?.id || "").toString().trim();
    const payload = {};
    if (shiprocketId) payload.id = shiprocketId;
    if (orderId) {
      payload.order_id = orderId;
      payload.orderId = orderId;
    }
    return Object.keys(payload).length ? payload : null;
  };

  const extractInvoiceUrl = (payload) => {
    if (!payload || typeof payload !== "object") return "";
    const direct =
      payload?.invoice_url ||
      payload?.invoiceUrl ||
      payload?.url ||
      payload?.pdf ||
      payload?.data?.invoice_url ||
      payload?.data?.invoiceUrl ||
      payload?.data?.url ||
      payload?.data?.pdf;
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    const list =
      payload?.invoice_urls ||
      payload?.invoiceUrls ||
      payload?.data?.invoice_urls ||
      payload?.data?.invoiceUrls;
    if (Array.isArray(list) && list.length) {
      const first = list.find((entry) => typeof entry === "string" && entry.trim());
      if (first) return first.trim();
    }
    return "";
  };

  const buildViewerUrl = (url) => {
    if (!url) return "";
    const encoded = encodeURIComponent(url);
    return `https://docs.google.com/viewerng/viewer?url=${encoded}`;
  };

  useEffect(() => {
    if (params?.id) {
      getRequest(`order/order_details/${params?.id}`, { headers: getAuthHeaders() })
        .then((res) => {
          if (res?.data) {
            setData(res?.data);
          }
        })
        .catch((err) => console.error("Order fetch failed:", err));
    }
  }, [params?.id]);

  const shiprocketMeta = {
    orderId:
      data?.order?.shiprocket_order_id ||
      data?.order?.shiprocketOrderId ||
      data?.order?.shiprocket_id ||
      data?.order?.sr_order_id ||
      data?.order?.srOrderId ||
      data?.order?.shiprocket_orderid ||
      "",
    awb:
      data?.order?.awb ||
      data?.order?.awb_number ||
      data?.order?.awbNo ||
      data?.order?.awb_no ||
      "",
    trackingUrl:
      data?.order?.tracking_url ||
      data?.order?.trackingUrl ||
      data?.order?.tracking_link ||
      data?.order?.trackingLink ||
      "",
  };

  useEffect(() => {
    if (!data || invoiceUrl || invoiceLoading) return;
    const payload = buildInvoicePayload();
    if (!payload) return;
    setInvoiceError("");
    setInvoiceLoading(true);
    shiprocketInvoice(payload)
      .then((res) => {
        const url = extractInvoiceUrl(res?.data || res);
        if (!url) throw new Error("Invoice URL not returned from Shiprocket.");
        setInvoiceUrl(url);
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch Shiprocket invoice.";
        setInvoiceError(String(msg));
      })
      .finally(() => setInvoiceLoading(false));
  }, [data, invoiceUrl, invoiceLoading]);

  useEffect(() => {
    if (data && typeof data === "object" && !invoiceItems.length) {
      const keys = Object.keys(data || {});
      console.warn("Invoice items not found. Response keys:", keys);
    }
  }, [data, invoiceItems.length]);

  return (
    <div className="w-full max-w-6xl mx-auto my-10 px-4">
      <div className="flex justify-center items-center mb-6">
        <h2 className="text-3xl font-bold" style={{ color: "#351F72" }}>
          Invoice
        </h2>
      </div>

      <div className="mb-4 text-sm text-gray-500">
        {invoiceLoading ? "Preparing Shiprocket invoice..." : ""}
      </div>

      {invoiceError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {invoiceError}
        </div>
      )}

      {invoiceUrl && (
        <div className="mb-6">
          <a
            href={buildViewerUrl(invoiceUrl)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-lg bg-[#351F72] px-5 py-2 text-sm font-semibold text-white shadow-md hover:bg-[#2a185c]"
          >
            Open Invoice In Browser
          </a>
        </div>
      )}



      <div
        ref={contentRef}
        className="bg-white shadow-lg rounded-xl p-6 border border-gray-200"
      >
        {/* Logo */}
        <div className="flex justify-start mb-6">
          <img src={opdlogo} alt="logo" className="w-40 h-auto" />
        </div>

        {/* Invoice Header */}
        <div className="flex flex-wrap justify-between mb-6">
          <div>
            <h5 className="text-lg font-medium text-gray-700">
              Invoice ID:{" "}
              <span className="font-bold text-[#351F72]">
                #{data?.order?.order_id}
              </span>
            </h5>
            <p className="text-gray-500 text-sm mt-1">
              Date:{" "}
              {data?.order?.createdAt &&
                new Date(data.order.createdAt).toLocaleDateString("en-IN", {
                  dateStyle: "medium",
                })}
            </p>
          </div>
          <div className="text-right">
            <h5 className="text-lg font-medium text-gray-700">From:</h5>
            <p className="text-[#351F72] font-extrabold">PC Care</p>
          </div>
        </div>

        {/* Billing Info */}
        <div className="flex flex-wrap justify-between border-t border-b py-4 mb-6">
          <div>
            <h5 className="text-lg font-medium text-gray-700">Billing To:</h5>
            <p className="text-gray-600">
              {data?.customerData?.name} <br />
              {data?.customerData?.address} <br />
              Mobile: {data?.customerData?.mobile}
            </p>
          </div>
        </div>

        {/* Shiprocket Tracking */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <h5 className="mb-2 text-base font-semibold text-[#351F72]">Shiprocket Details</h5>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <span className="text-xs text-slate-500">Shiprocket Order ID</span>
              <p className="font-medium">{shiprocketMeta.orderId || "-"}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500">AWB</span>
              <p className="font-medium">{shiprocketMeta.awb || "-"}</p>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-xs text-slate-500">Tracking</span>
            {shiprocketMeta.trackingUrl ? (
              <a
                href={shiprocketMeta.trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="ml-2 font-semibold text-[#351F72] underline"
              >
                Track Shipment
              </a>
            ) : (
              <span className="ml-2 text-slate-500">Not available yet</span>
            )}
          </div>
        </div>

        {/* Invoice Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <thead className="bg-[#351F72] text-white">
              <tr>
                <th className="p-3 border border-gray-300 text-left">
                  Product Name
                </th>
                <th className="p-3 border border-gray-300 text-center">
                  Quantity
                </th>
                <th className="p-3 border border-gray-300 text-right">
                  Price
                </th>
                <th className="p-3 border border-gray-300 text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {invoiceItems?.length ? (
                invoiceItems.map((item, index) => (
                  <tr key={index} className="hover:bg-[#351F72]/10">
                    <td className="p-3 border border-gray-300 text-gray-700">
                      {item?.product?.name ||
                        item?.product_name ||
                        item?.name ||
                        "Product"}
                    </td>
                    <td className="p-3 border border-gray-300 text-center text-gray-700">
                      {getItemQty(item)}
                    </td>
                    <td className="p-3 border border-gray-300 text-right text-gray-700">
                      {formatCurrency(getItemPrice(item))}
                    </td>
                    <td className="p-3 border border-gray-300 text-right text-gray-800 font-medium">
                      {formatCurrency(getItemPrice(item) * getItemQty(item))}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="p-4 text-center text-sm text-gray-500">
                    No invoice items found for this order.
                  </td>
                </tr>
              )}
              <tr className="bg-gray-100 font-semibold">
                <td colSpan="3" className="p-3 border border-gray-300 text-left">
                  Sub Total:
                </td>
                <td className="p-3 border border-gray-300 text-right">
                  {formatCurrency(invoiceTotal)}
                </td>
              </tr>
              <tr className="bg-gray-200 font-bold">
                <td colSpan="3" className="p-3 border border-gray-300 text-left">
                  Total Amount:
                </td>
                <td className="p-3 border border-gray-300 text-right text-black">
                  {formatCurrency(invoiceTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyorderDetails;
